import { Box, Flex, Stack, Text, Group, Divider, Skeleton } from '@mantine/core';
import type { LabOrganization, TestCoding } from '@medplum/health-gorilla-core';
import type { Questionnaire, QuestionnaireItem, QuestionnaireResponse } from '@medplum/fhirtypes';
import { AsyncAutocomplete, QuestionnaireForm } from '@medplum/react';
import type { AsyncAutocompleteOption } from '@medplum/react';
import { useMemo, useCallback, useState, type ReactNode } from 'react';
import { BARIENDO_TEST_GROUP_SYSTEM } from '@bariendo/lib/constants';
import classes from './LabOrderMessageBox.module.css';
import { TestSelectionCard } from '../TestSelectionCard';
import { QUESTIONNAIRE_LINK_IDS } from './constants';
import { BARIENDO_TEST_GROUP_ITEM_EXTENSION } from '@bariendo/lib/extensions';
import { parseTestEntry } from './parseTestEntry';

interface TestGroup {
  code: string;
  display: string;
  testCodes: string[];
}

interface LabOrderQuestionnaireSectionProps {
  questionnaire: Questionnaire;
  questionnaireResponse: QuestionnaireResponse;
  performingLab: LabOrganization | undefined;
  selectedTests: TestCoding[];
  testMetadata: Record<string, any>;
  searchAvailableLabs: (query: string) => Promise<LabOrganization[]>;
  searchAvailableTests: (query: string) => Promise<TestCoding[]>;
  onQuestionnaireChange: (response: QuestionnaireResponse) => void;
  onLabChange: (labs: LabOrganization[]) => void;
  onTestsChange: (tests: TestCoding[]) => void;
  isAutoSelectingTests: boolean;
  /** Rendered in place of the questionnaire's own requester item, which cannot set the order's requester. */
  orderingProviderField: ReactNode;
}

interface QuestionnaireItemsBlockProps {
  items: QuestionnaireItem[];
  questionnaire: Questionnaire;
  questionnaireResponse: QuestionnaireResponse;
  onQuestionnaireChange: (response: QuestionnaireResponse) => void;
}

function QuestionnaireItemsBlock({
  items,
  questionnaire,
  questionnaireResponse,
  onQuestionnaireChange,
}: QuestionnaireItemsBlockProps) {
  if (items.length === 0) {
    return null;
  }

  return (
    <Box className={classes.questionnaireWrapper}>
      <QuestionnaireForm
        questionnaire={{
          ...questionnaire,
          title: undefined,
          item: items,
        }}
        questionnaireResponse={questionnaireResponse}
        excludeButtons={true}
        onChange={onQuestionnaireChange}
      />
    </Box>
  );
}

export function LabOrderQuestionnaireSection({
  questionnaire,
  questionnaireResponse,
  performingLab,
  selectedTests,
  testMetadata,
  searchAvailableLabs,
  searchAvailableTests,
  onQuestionnaireChange,
  onLabChange,
  onTestsChange,
  isAutoSelectingTests,
  orderingProviderField,
}: LabOrderQuestionnaireSectionProps) {
  const [isExpandingGroup, setIsExpandingGroup] = useState(false);

  // Convert TestCoding to AsyncAutocomplete option (handles both tests and groups)
  const testToOption = (test: TestCoding): AsyncAutocompleteOption<TestCoding> => ({
    value: test.code,
    label: test.display || test.code,
    resource: test,
  });

  // Custom component for rendering dropdown items
  const TestOptionItem = (props: AsyncAutocompleteOption<TestCoding>) => {
    const isGroup = props.value.startsWith('group:');

    if (isGroup) {
      return (
        <Group gap="xs">
          {props.label}
          <Divider style={{ flex: 1 }} color="#7a7c7eff" />
        </Group>
      );
    }

    return <>{props.label}</>;
  };

  // Convert LabOrganization to AsyncAutocomplete option
  const labToOption = (lab: LabOrganization): AsyncAutocompleteOption<LabOrganization> => ({
    value: lab.id || '',
    label: lab.name || 'Unknown Lab',
    resource: lab,
  });

  // Extract test groups from questionnaire
  const testGroups = useMemo<TestGroup[]>(() => {
    const testGroupsItem = questionnaire?.item?.find((item) => item.linkId === QUESTIONNAIRE_LINK_IDS.TEST_GROUPS);
    if (!testGroupsItem?.answerOption) return [];

    return testGroupsItem.answerOption
      .map((option) => {
        const coding = option.valueCoding;
        const testsExtension = option.extension?.find((ext) => ext.url === BARIENDO_TEST_GROUP_ITEM_EXTENSION);
        const testCodes = testsExtension?.valueString?.split(',').map((code) => code.trim()) || [];

        if (!coding?.code || !coding.display) return null;

        return {
          code: coding.code,
          display: coding.display,
          testCodes,
        };
      })
      .filter((group): group is TestGroup => group !== null);
  }, [questionnaire]);

  // Merged search: both test groups and individual tests
  const searchTestsWithGroups = useCallback(
    async (query: string): Promise<TestCoding[]> => {
      // Search individual tests
      const individualTests = await searchAvailableTests(query);

      // Filter test groups
      const lowerQuery = query.toLowerCase();
      const matchingGroups = testGroups.filter(
        (group) => !query.trim() || group.display.toLowerCase().includes(lowerQuery)
      );

      // Convert groups to test-like options and prepend them
      const groupOptions = matchingGroups.map((group) => ({
        code: `group:${group.code}`,
        display: group.display,
        system: BARIENDO_TEST_GROUP_SYSTEM,
      })) as TestCoding[];

      return [...groupOptions, ...individualTests];
    },
    [searchAvailableTests, testGroups]
  );

  // Handle test selection - expand groups or add individual tests
  const handleTestsChange = useCallback(
    (selectedItems: TestCoding[]) => {
      // Separate groups from individual tests
      const groups: string[] = [];
      const individualTests: TestCoding[] = [];

      selectedItems.forEach((item) => {
        if (item.code.startsWith('group:')) {
          groups.push(item.code.replace('group:', ''));
        } else {
          individualTests.push(item);
        }
      });

      // If groups were selected, expand them asynchronously
      if (groups.length > 0) {
        // Set loading state ONLY if groups need to be expanded
        setIsExpandingGroup(true);

        // Then expand groups in the background and add tests
        (async () => {
          const testsToAdd: TestCoding[] = [];

          for (const groupCode of groups) {
            const group = testGroups.find((g) => g.code === groupCode);
            if (!group) {
              // eslint-disable-next-line no-console
              console.warn(`Test group not found: ${groupCode}`);
              continue;
            }

            // eslint-disable-next-line no-console
            console.log(
              `Expanding test group "${group.display}" with ${group.testCodes.length} tests:`,
              group.testCodes
            );

            // Search for each test in the group (by display name, not code)
            for (const entry of group.testCodes) {
              try {
                const { searchName, preferredCode } = parseTestEntry(entry);
                if (!searchName) {
                  continue;
                }

                // Search by display name since HealthGorilla uses internal IM codes
                const results = await searchAvailableTests(searchName);

                const preferredMatch = preferredCode ? results.find((test) => test.code === preferredCode) : undefined;
                if (preferredMatch) {
                  if (!testsToAdd.some((t) => t.code === preferredMatch.code)) {
                    testsToAdd.push(preferredMatch);
                    // eslint-disable-next-line no-console
                    console.log(
                      `✓ Added preferred test: ${preferredMatch.display} (${preferredMatch.code}) for "${searchName}"`
                    );
                  }
                  continue;
                }

                const lowerTestName = searchName.toLowerCase().replace(/-/g, ' ');
                const matchingTest = results.find((test) => {
                  const testDisplay = test.display?.toLowerCase() || '';
                  return testDisplay === lowerTestName || testDisplay.startsWith(lowerTestName);
                });

                if (matchingTest) {
                  if (!testsToAdd.some((t) => t.code === matchingTest.code)) {
                    testsToAdd.push(matchingTest);
                    // eslint-disable-next-line no-console
                    console.log(
                      `✓ Added test: ${matchingTest.display} (${matchingTest.code}) for search "${searchName}"`
                    );
                  }
                } else {
                  // eslint-disable-next-line no-console
                  console.warn(`✗ Test not found for: "${searchName}" (searched in ${results.length} results)`);
                }
              } catch (error) {
                // eslint-disable-next-line no-console
                console.error(`Failed to search for test: ${entry}`, error);
              }
            }
          }

          // eslint-disable-next-line no-console
          console.log(`Expansion complete. Total tests: ${testsToAdd.length}`);

          // Update with all tests after expansion
          onTestsChange(testsToAdd);
          setIsExpandingGroup(false);
        })();
      } else {
        // No groups, just update with individual tests
        onTestsChange(individualTests);
      }
    },
    [testGroups, searchAvailableTests, onTestsChange]
  );

  // Partition questionnaire items to insert custom components at specific positions
  const partitionedItems = useMemo<{
    before: QuestionnaireItem[];
    between: QuestionnaireItem[];
    after: QuestionnaireItem[];
  }>(() => {
    if (!questionnaire?.item) {
      return { before: [], between: [], after: [] };
    }

    const items = questionnaire.item;
    const performingLabIndex = items.findIndex((item) => item.linkId === QUESTIONNAIRE_LINK_IDS.PERFORMING_LAB);
    const selectedTestsIndex = items.findIndex((item) => item.linkId === QUESTIONNAIRE_LINK_IDS.SELECTED_TESTS);

    // Items before 'performing-lab', minus the requester, which orderingProviderField replaces
    const before = (performingLabIndex >= 0 ? items.slice(0, performingLabIndex) : []).filter(
      (item) => item.linkId !== QUESTIONNAIRE_LINK_IDS.REQUESTER
    );

    // Items between 'performing-lab' and 'selected-tests'
    const between =
      performingLabIndex >= 0 && selectedTestsIndex >= 0
        ? items.slice(performingLabIndex + 1, selectedTestsIndex)
        : performingLabIndex >= 0 && selectedTestsIndex < 0
          ? items.slice(performingLabIndex + 1)
          : [];

    // Items after 'selected-tests'
    const after = selectedTestsIndex >= 0 ? items.slice(selectedTestsIndex + 1) : [];

    return { before, between, after };
  }, [questionnaire]);

  return (
    <Stack gap="md">
      {orderingProviderField}

      {/* Items before 'performing-lab' */}
      <QuestionnaireItemsBlock
        items={partitionedItems.before}
        questionnaire={questionnaire}
        questionnaireResponse={questionnaireResponse}
        onQuestionnaireChange={onQuestionnaireChange}
      />

      {/* Custom HealthGorilla Lab Selector */}
      <AsyncAutocomplete<LabOrganization>
        key={performingLab?.id || 'no-lab'}
        label="Performing Laboratory"
        placeholder="Search for a lab..."
        maxValues={1}
        loadOptions={searchAvailableLabs}
        toOption={labToOption}
        onChange={onLabChange}
        defaultValue={performingLab ? [performingLab] : []}
        required
      />

      {/* Items between 'performing-lab' and 'selected-tests' (exclude test-groups) */}
      <QuestionnaireItemsBlock
        items={partitionedItems.between.filter((item) => item.linkId !== QUESTIONNAIRE_LINK_IDS.TEST_GROUPS)}
        questionnaire={questionnaire}
        questionnaireResponse={questionnaireResponse}
        onQuestionnaireChange={onQuestionnaireChange}
      />

      {/* Merged Test Selector (groups + individual tests) */}
      <AsyncAutocomplete<TestCoding>
        key={selectedTests.map((t) => t.code).join(',')}
        label={`Selected Tests ${selectedTests.length > 0 ? `(${selectedTests.length})` : ''}`}
        placeholder="Type to search tests or select test panels..."
        disabled={!performingLab || isExpandingGroup || isAutoSelectingTests}
        maxValues={20}
        loadOptions={searchTestsWithGroups}
        toOption={testToOption}
        onChange={handleTestsChange}
        defaultValue={selectedTests}
        itemComponent={TestOptionItem}
        description={
          !performingLab
            ? 'Select a lab first'
            : isAutoSelectingTests
              ? 'Loading default tests...'
              : isExpandingGroup
                ? 'Loading test panel...'
                : 'Search for individual tests or select test panels'
        }
        required
      />

      {/* Loading skeletons during auto-selection or group expansion */}
      {(isAutoSelectingTests || isExpandingGroup) && (
        <Box>
          <Text size="sm" fw={500} mb="sm">
            Loading tests...
          </Text>
          <Flex gap="md" wrap="wrap">
            {[1, 2, 3].map((i) => (
              <Box key={i} style={{ width: '300px' }}>
                <Skeleton height={200} radius="md" />
              </Box>
            ))}
          </Flex>
        </Box>
      )}

      {/* Selected tests grid */}
      {!isExpandingGroup && selectedTests.length > 0 && (
        <Box>
          <Flex gap="md" wrap="wrap">
            {selectedTests.map((test) => {
              const metadata = testMetadata[test.code];
              return <TestSelectionCard key={test.code} test={test} metadata={metadata} />;
            })}
          </Flex>
        </Box>
      )}

      {/* Items after 'selected-tests' */}
      <QuestionnaireItemsBlock
        items={partitionedItems.after}
        questionnaire={questionnaire}
        questionnaireResponse={questionnaireResponse}
        onQuestionnaireChange={onQuestionnaireChange}
      />
    </Stack>
  );
}
